/**
 * @file NotifyProvider.tsx
 * @description Sonner-адаптер с `useNotify` API (phase08.5).
 *
 * Сохраняет публичный API из phase06 (useNotify().success/error/info/warning),
 * чтобы не пере-импортировать во всех страницах. Внутри — sonner toast.
 *
 * Mount единый `<Toaster />` в корне приложения (см. main.tsx).
 *
 * @example
 *   const notify = useNotify();
 *   notify.error("Не удалось сохранить");
 *   notify.success("Сохранено");
 */

import { toast } from "sonner";
import * as React from "react";

export { Toaster } from "./sonner";

export type NotifySeverity = "error" | "success" | "info" | "warning";

/**
 * Кнопка в тосте: «Открыть документ» после проведения размещения
 * (phase19.24 этап 1, шаг 4). Нужна там, где действие создало документ, а
 * человек остался на другом экране — иначе номер MV приходится искать руками.
 */
export interface NotifyAction {
  label: string;
  onClick: () => void;
}

interface NotifyOptions {
  action?: NotifyAction;
  /** Сколько держать тост, мс (Р3а: «Страница опубликована. [Вернуть как было]» — 8 с). */
  duration?: number;
}

interface NotifyContextValue {
  error: (message: string) => void;
  success: (message: string, options?: NotifyOptions) => void;
  info: (message: string) => void;
  /** С кнопкой — например, «Открыть PDF в новой вкладке», когда печать из страницы не удалась. */
  warning: (message: string, options?: NotifyOptions) => void;
}

const notifyApi: NotifyContextValue = {
  success: (m, options) => toast.success(m, { action: options?.action, ...(options?.duration ? { duration: options.duration } : {}) }),
  error: (m) => toast.error(m, { duration: 8000 }),
  info: (m) => toast.info(m),
  warning: (m, options) => toast.warning(m, options?.action ? { action: options.action, duration: 15_000 } : undefined),
};

/** Hook совместимый со старым API. */
export function useNotify(): NotifyContextValue {
  return notifyApi;
}

/**
 * Адаптер-пропускающий компонент: оставлен для обратной совместимости
 * (раньше в main.tsx был <NotifyProvider>, теперь не нужен). Просто рендерит
 * children. Реальный Toaster подключается отдельно в main.tsx.
 */
export function NotifyProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
